import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Plus, Pencil, Archive, ListChecks, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

const CATEGORY_OPTIONS = [
  { value: 'standard', label: i18n.t('auto.lbl.standard') },
  { value: 'extra', label: i18n.t('auto.lbl.extraOutOfScope2') },
  { value: 'rework', label: i18n.t('auto.lbl.rework') },
];

const empty = { name: '', description: '', trade: '', category: 'standard', est_hours: 0, est_workers: 1, unit: '', sort_order: 0 };

const TaskTemplateSettings = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [trades, setTrades] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [showArchived, setShowArchived] = useState(false);
  const [newTrade, setNewTrade] = useState('');

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/task-templates`, { params: { include_archived: showArchived } }),
        axios.get(`${API}/settings`),
      ]);
      setItems(r.data || []);
      setTrades(s.data?.task_trades || []);
    } catch (e) {
      toast.error(t('auto.set.failedToLoadTaskTemplates'));
    }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const addTrade = async () => {
    const v = newTrade.trim();
    if (!v) return;
    if (trades.some((t) => t.toLowerCase() === v.toLowerCase())) {
      toast.error(t('auto.set.tradeAlreadyExists'));
      return;
    }
    try {
      const next = [...trades, cap(v)];
      await axios.put(`${API}/settings/task_trades`, next);
      toast.success(t('auto.set.tradeAdded'));
      setNewTrade('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add trade');
    }
  };

  const removeTrade = async (val) => {
    if (!window.confirm(`Remove trade "${val}"? Existing tasks won't be affected.`)) return;
    try {
      const next = trades.filter((t) => t !== val);
      await axios.put(`${API}/settings/task_trades`, next);
      toast.success(t('auto.set.tradeRemoved'));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove trade');
    }
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...empty, ...t, trade: t.trade || '' }); setOpen(true); };

  const save = async () => {
    if (!form.name?.trim()) { toast.error(t('auto.set.nameIsRequired')); return; }
    const body = {
      name: form.name.trim(),
      description: form.description || null,
      trade: form.trade || null,
      category: form.category,
      est_hours: parseFloat(form.est_hours) || 0,
      est_workers: parseInt(form.est_workers) || 1,
      unit: form.unit || null,
      sort_order: parseInt(form.sort_order) || 0,
    };
    try {
      if (editing) await axios.put(`${API}/task-templates/${editing.id}`, body);
      else await axios.post(`${API}/task-templates`, body);
      toast.success(editing ? t('auto.set.templateUpdated') : t('auto.set.templateCreated'));
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const archive = async (tItem) => {
    if (!window.confirm(`Archive template "${tItem.name}"?`)) return;
    try {
      await axios.delete(`${API}/task-templates/${tItem.id}`);
      toast.success(t('auto.set.templateArchived'));
      load();
    } catch (e) {
      toast.error(t('auto.set.archiveFailed'));
    }
  };

  return (
    <div className="space-y-4" data-testid="task-templates-settings">
      {/* Trades editor */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold">{t('auto.set.trades')}</h3>
            <span className="text-xs text-gray-500">{t('auto.set.usedAsTheTradeDropdownWhen')}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {trades.length === 0 && <span className="text-sm text-gray-500">{t('auto.set.noTradesConfigured')}</span>}
            {trades.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 text-sm" data-testid={`trade-pill-${t}`}>
                {cap(t)}
                <button onClick={() => removeTrade(t)} className="text-gray-400 hover:text-red-600" aria-label={`Remove ${t}`} data-testid={`remove-trade-${t}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 max-w-md">
            <Input
              value={newTrade}
              onChange={(e) => setNewTrade(e.target.value)}
              placeholder="e.g. Insulation"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTrade(); } }}
              data-testid="new-trade-input"
            />
            <Button onClick={addTrade} className="bg-[#AA0000] hover:bg-[#880000]" data-testid="add-trade-btn">
              <Plus className="h-4 w-4 mr-1" />{t('auto.set.add')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <ListChecks className="h-5 w-5 text-[#AA0000] shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h3 className="text-lg font-semibold">{t('auto.set.taskTemplates')}</h3>
            <span className="text-xs text-gray-500">{t('auto.set.reusableTaskDefinitionsForProjectPlanning')}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <label className="text-sm flex items-center gap-1">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            {t('auto.set.showArchived')}
          </label>
          <Button onClick={openNew} className="bg-[#AA0000] hover:bg-[#880000]" data-testid="new-task-template-btn">
            <Plus className="h-4 w-4 mr-1" /> {t('auto.set.newTemplate')}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3">{t('auto.set.name')}</th>
                <th className="text-left p-3">{t('auto.set.trade')}</th>
                <th className="text-left p-3">{t('auto.set.category')}</th>
                <th className="text-right p-3">{t('auto.set.estHours')}</th>
                <th className="text-right p-3">{t('auto.set.workers')}</th>
                <th className="text-left p-3">{t('auto.set.unit')}</th>
                <th className="text-right p-3">{t('auto.set.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={7} className="text-center text-gray-500 p-6">{t('auto.set.noTemplatesYetCreateYourFirst')}</td></tr>
              )}
              {items.map((t) => (
                <tr key={t.id} className={`border-b ${t.is_archived ? 'opacity-50' : ''}`}>
                  <td className="p-3">
                    <div className="font-medium">{t.name}</div>
                    {t.description && <div className="text-xs text-gray-500">{t.description}</div>}
                  </td>
                  <td className="p-3">{cap(t.trade) || '—'}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${t.category === 'extra' ? 'bg-orange-100 text-orange-700' : t.category === 'rework' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                      {t.category}
                    </span>
                  </td>
                  <td className="p-3 text-right tabular-nums">{(t.est_hours || 0).toFixed(2)}</td>
                  <td className="p-3 text-right tabular-nums">{t.est_workers || 1}</td>
                  <td className="p-3 text-gray-600">{t.unit || '—'}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-task-template-${t.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {!t.is_archived && (
                      <Button size="sm" variant="ghost" onClick={() => archive(t)} data-testid={`archive-task-template-${t.id}`}>
                        <Archive className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="task-template-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t('auto.set.editTemplate') : t('auto.set.newTaskTemplate')}</DialogTitle>
            <DialogDescription>{t('auto.set.defineAReusableTaskWithDefault')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('auto.set.name2')}</Label>
              <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value  }))} data-testid="task-template-name" />
            </div>
            <div>
              <Label>{t('auto.set.description')}</Label>
              <Input value={form.description || ''} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value  }))} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>{t('auto.set.trade')}</Label>
                <Select value={form.trade || '__none__'} onValueChange={(v) => setForm(prev => ({ ...prev, trade: v === '__none__' ? '' : v  }))}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {trades.map((t) => <SelectItem key={t} value={t}>{cap(t)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('auto.set.category')}</Label>
                <Select value={form.category} onValueChange={(v) => setForm(prev => ({ ...prev, category: v  }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('auto.set.estHours')}</Label>
                <Input type="number" step="0.25" value={form.est_hours} onChange={(e) => setForm(prev => ({ ...prev, est_hours: e.target.value  }))} />
              </div>
              <div>
                <Label>{t('auto.set.estWorkers')}</Label>
                <Input type="number" min="1" value={form.est_workers} onChange={(e) => setForm(prev => ({ ...prev, est_workers: e.target.value  }))} />
              </div>
              <div>
                <Label>Unit (e.g. per panel)</Label>
                <Input value={form.unit || ''} onChange={(e) => setForm(prev => ({ ...prev, unit: e.target.value  }))} />
              </div>
              <div>
                <Label>{t('auto.set.sortOrder')}</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm(prev => ({ ...prev, sort_order: e.target.value  }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="dialog-cancel-btn">{t('auto.set.cancel')}</Button>
            <Button onClick={save} className="bg-[#AA0000] hover:bg-[#880000]" data-testid="save-task-template-btn">{t('auto.set.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskTemplateSettings;
